import { Box, Card, CardContent, Chip, Grid, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type React from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useBilling } from '../../contexts/BillingContext';

interface MonthlyUsage {
  tenders?: number;
  drafts?: number;
  reports?: number;
  reviews?: number;
  adhoc_documents?: number;
  generated_cvs?: number;
  section_chats?: number;
}

const AdminOverviewPanel: React.FC = () => {
  const theme = useTheme();
  const { account } = useAccount();
  const { ledger } = useBilling();

  // Helper to get current monthly usage
  const getCurrentUsage = (): MonthlyUsage => {
    if (!account?.usage?.monthly) {
      return {};
    }

    // Sort keys (dates) to find the active period
    const keys = Object.keys(account.usage.monthly).sort();
    const today = new Date().toISOString().split('T')[0] || '';

    // Assuming keys are period end dates, find the first one >= today
    // If no future key exists (e.g. at end of cycle before rollover), fallback to latest
    const currentKey = keys.find((k) => k >= today) || keys[keys.length - 1];

    return (currentKey ? account.usage.monthly[currentKey] : {}) as MonthlyUsage;
  };

  const currentUsage = getCurrentUsage();

  const getWelcomeInfo = () => {
    if (!account) {
      return {
        title: 'Admin Panel',
        description: 'Administrative functions available to you.',
        scope: 'Limited',
        color: 'default' as const,
      };
    }
    // Using explicit roles for UI tailoring is acceptable as long as access is controlled by permissions
    switch (account.role) {
      case 'client_admin':
        return {
          title: 'Organization Administration',
          description: `Manage users and settings for ${account?.clientName || 'your organization'}.`,
          scope: account?.clientId || 'Organization',
          color: 'warning' as const,
        };
      case 'team_lead':
        return {
          title: 'Team Management',
          description: 'Manage your team members and departmental settings.',
          scope: 'Team/Department',
          color: 'info' as const,
        };
      default:
        // Even 'user' role might see this page if they have permissions (e.g. Support Staff)
        return {
          title: 'Admin Panel',
          description: 'Administrative functions available to you.',
          scope: 'Limited',
          color: 'default' as const,
        };
    }
  };

  const welcomeInfo = getWelcomeInfo();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ pb: 2 }}>
        <Grid container>
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              borderRight: { md: `1px solid ${theme.palette.divider}` },
              pr: { md: 3 },
              mb: { xs: 3, md: 0 },
            }}
          >
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mt: 0 }}>
                {welcomeInfo.title}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ fontSize: '1rem', mb: 2 }}
              >
                {welcomeInfo.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Access Level:
                </Typography>
                <Chip
                  label={account?.role?.replace('_', ' ').toUpperCase()}
                  color={welcomeInfo.color}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Logged in as
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  {account?.displayName || account?.email}
                </Typography>
                {account?.clientName && (
                  <Typography variant="subtitle1" color="text.primary">
                    {account.clientName}
                  </Typography>
                )}
              </Box>

              {account?.role === 'client_admin' && account?.subscription && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Organization Limits
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {[
                      {
                        label: 'Users',
                        usage: account.clientUserCount ?? 0,
                        limit: account.subscription.limits?.numUsers ?? 1,
                      },
                      {
                        label: 'Teams',
                        usage:
                          'clientTeamCount' in account ? (account.clientTeamCount as number) : '-',
                        limit:
                          account.subscription.limits?.numTeams ??
                          ((account as unknown as Record<string, unknown>)?.maxTeams as
                            | number
                            | undefined) ??
                          1,
                      },
                    ].map((item) => {
                      const limitVal = item.limit ?? Infinity;
                      const isUnlimited = limitVal === Infinity || item.limit === undefined;
                      const usageRatio =
                        !isUnlimited && typeof item.usage === 'number'
                          ? item.usage / (limitVal as number)
                          : 0;

                      let bg = 'transparent';
                      if (!isUnlimited && typeof item.usage === 'number') {
                        if (usageRatio >= 0.9) {
                          bg = alpha(theme.palette.error.main, 0.1);
                        } else if (usageRatio >= 0.75) {
                          bg = alpha(theme.palette.warning.main, 0.1);
                        } else if (usageRatio >= 0.5) {
                          bg = alpha(theme.palette.warning.light, 0.1);
                        }
                      }

                      return (
                        <Box
                          key={item.label}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: bg,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {item.label}
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {`${item.usage} / ${isUnlimited ? '∞' : item.limit}`}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ pl: { md: 3 } }}>
            {account?.role === 'client_admin' && account?.subscription ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
                {/* Monthly Feature Limits or Usage Status */}
                <Box
                  sx={{
                    bgcolor: 'background.default',
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  {(ledger?.isPAYG ?? account.subscription.billing_model === 'payg') ? (
                    <>
                      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                        Usage-Based Billing
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your organization is on a usage-based billing model. Resources are billed in
                        arrears based on usage without strict monthly caps.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                        Monthly Feature Limits
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[
                          {
                            label: 'Tender Pursuits',
                            usage: currentUsage.tenders ?? 0,
                            limit:
                              account.subscription.limits?.numTenders ??
                              account.subscription.maxTenders,
                          },
                          {
                            label: 'Drafts/Proposals',
                            usage: currentUsage.drafts ?? 0,
                            limit: account.subscription.limits?.numDrafts,
                          },
                          {
                            label: 'Reviews',
                            usage: currentUsage.reviews ?? 0,
                            limit: account.subscription.limits?.numReviews,
                          },
                          {
                            label: 'Capability Reports',
                            usage: currentUsage.reports ?? 0,
                            limit: account.subscription.limits?.numReports,
                          },
                          {
                            label: 'AI Chat Messages',
                            usage: currentUsage.section_chats ?? 0,
                            limit: account.subscription.limits?.numSectionChats,
                          },
                        ].map((item) => {
                          const limitVal = item.limit ?? Infinity;
                          const isUnlimited = limitVal === Infinity || item.limit === undefined;
                          const usageRatio =
                            !isUnlimited && item.usage ? item.usage / (limitVal as number) : 0;

                          let bg = 'transparent';
                          if (!isUnlimited && item.usage) {
                            if (usageRatio >= 0.9) {
                              bg = alpha(theme.palette.error.main, 0.1);
                            } else if (usageRatio >= 0.75) {
                              bg = alpha(theme.palette.warning.main, 0.1);
                            } else if (usageRatio >= 0.5) {
                              bg = alpha(theme.palette.warning.light, 0.1);
                            }
                          }

                          return (
                            <Box
                              key={item.label}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1,
                                borderRadius: 1,
                                bgcolor: bg,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              <Typography variant="body2" fontWeight="medium">
                                {item.label}
                              </Typography>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {`${item.usage} / ${isUnlimited ? '∞' : item.limit}`}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                }}
              >
                <Typography color="text.secondary">No subscription details available.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AdminOverviewPanel;
